import { useState } from 'react';
import axios from 'axios';
import { AlertCircle, Info, Loader2, Scale } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { apiClient } from '@/lib/api-client';
import { Alert, AlertDescription } from '@/app/components/ui/alert';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Checkbox } from '@/app/components/ui/checkbox';
import { Label } from '@/app/components/ui/label';

interface LegalAcceptanceGateProps {
  children: React.ReactNode;
}

export function LegalAcceptanceGate({ children }: LegalAcceptanceGateProps) {
  const { user, refreshUser, logout } = useAuth();
  const [accepted, setAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const legal = user?.legal_acceptance;
  if (!legal?.required) {
    return <>{children}</>;
  }

  const changedDocuments = legal.changed_documents.join(' and ');
  const organizationName = legal.organization_name ?? 'your organization';

  if (legal.awaiting_organization_admin) {
    return (
      <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-xl">
          <CardHeader className="space-y-3 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600">
              <Scale className="h-7 w-7 text-white" />
            </div>
            <CardTitle>Waiting for your admin</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                An admin of {organizationName} needs to accept the{' '}
                {legal.is_update ? 'updated ' : ''}Terms of Service and Privacy
                Policy before you can continue. Please ask your admin to sign
                in and accept them, then check again.
              </AlertDescription>
            </Alert>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                className="flex-1"
                onClick={() => void refreshUser()}
              >
                Check again
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => void logout()}
              >
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>
    );
  }

  const submitAcceptance = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await apiClient.post('/api/v1/auth/legal-acceptance/', {
        accepted: true,
      });
      await refreshUser();
    } catch (err) {
      if (
        axios.isAxiosError(err) &&
        err.response?.data?.code === 'organization_acceptance_required'
      ) {
        // The organization still needs an admin's acceptance; refresh so
        // the "ask your admin" screen is shown instead of an error.
        await refreshUser();
        return;
      }
      setError('We could not record your acceptance. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-xl">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-lg bg-blue-600">
            <Scale className="h-7 w-7 text-white" />
          </div>
          <CardTitle>
            {legal.is_update
              ? `We've updated our ${changedDocuments}`
              : 'Please accept our Terms of Service and Privacy Policy'}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Please review and accept the current documents before continuing.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="flex items-start gap-3 rounded-lg border p-4">
            <Checkbox
              id="legal-acceptance"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
              disabled={isSubmitting}
            />
            <Label
              htmlFor="legal-acceptance"
              className="cursor-pointer text-sm font-normal leading-6"
            >
              I have read and agree to the{' '}
              <a
                href={legal.terms_url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-blue-600 underline"
              >
                Terms of Service
              </a>{' '}
              and{' '}
              <a
                href={legal.privacy_url}
                target="_blank"
                rel="noreferrer"
                className="font-medium text-blue-600 underline"
              >
                Privacy Policy
              </a>
              {legal.accepts_for_organization && legal.organization_name
                ? `, and I'm authorised to accept them on behalf of ${legal.organization_name}`
                : ''}
              .
            </Label>
          </div>
          <Button
            type="button"
            className="w-full"
            disabled={!accepted || isSubmitting}
            onClick={submitAcceptance}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Continue
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
